/**
 * Mirrors apps/backend/src/modules/user/roles.ts.
 * Kept in sync manually.
 *
 * One Partner Portal role per person. WorkOS names them `partner_portal_*`;
 * `/api/auth/me` sends them without that prefix, so this file never sees it.
 * `admin` is the wildcard.
 *
 * Roles are assigned on the Myela Dashboard's Settings → Team page, not here.
 */

export const ROLES = [
  "admin",
  "staff",
  "finance",
  "manager",
  "support",
  "viewer",
  "agent",
  "sub_agent",
  "godaddy_merchant",
] as const;

export type AppRole = (typeof ROLES)[number];

export const ROLE_DESCRIPTIONS: Record<AppRole, { label: string; description: string }> = {
  admin: { label: "Admin", description: "Everything, including commission settings and the Users page." },
  staff: { label: "Staff", description: "The whole portal except commissions." },
  finance: { label: "Finance", description: "Staff access plus commissions." },
  manager: { label: "Manager", description: "Staff access; no extra rules yet." },
  support: { label: "Support", description: "Staff access; no extra rules yet." },
  viewer: { label: "Viewer", description: "Staff access; not read-only yet." },
  agent: { label: "Agent", description: "Their own merchants and applications." },
  sub_agent: { label: "Sub-agent", description: "Works under a parent agent." },
  godaddy_merchant: { label: "GoDaddy merchant", description: "Poynt Collect and Helpdesk only." },
};

// Who an announcement can target; matched against the reader's userType on the server.
export const AUDIENCES = ["internal", "agent", "sub_agent", "godaddy_merchant"] as const;

export type Audience = (typeof AUDIENCES)[number];

export const AUDIENCE_LABELS: Record<Audience, string> = {
  internal: "Internal",
  agent: "Agent",
  sub_agent: "Sub-agent",
  godaddy_merchant: "GoDaddy merchant",
};

// The Partner Portal roles that may see the Merchants section; agents, sub-agents and GoDaddy merchants never do.
const MERCHANT_VIEW_ROLES: readonly AppRole[] = ["admin", "staff", "finance", "manager", "support", "viewer"];
const NO_MERCHANTS_ROLES: readonly AppRole[] = ["agent", "sub_agent", "godaddy_merchant"];

export function canSeeMerchants(userRoles: string[] | undefined | null): boolean {
  const roles = (userRoles ?? []) as AppRole[];
  return (
    roles.some((r) => MERCHANT_VIEW_ROLES.includes(r)) &&
    !roles.some((r) => NO_MERCHANTS_ROLES.includes(r))
  );
}

export function hasRole(
  userRoles: string[] | undefined | null,
  required: AppRole | AppRole[],
): boolean {
  if (!userRoles) return false;
  // No wildcard — mirrors the backend (modules/user/roles.ts). `admin` used to
  // satisfy every check, granting things nobody had written down.
  const allowed = Array.isArray(required) ? required : [required];
  return allowed.some((r) => userRoles.includes(r));
}
